import { DEFAULT_COMPANY_TIME_ZONE, platformPlanBody, zonedDateKey, zonedParts, type PlanId, type PlanLimitsDto, type PlatformPlanBody, type PlatformPlanDto } from '@dbrain/shared';
import { licenseDay, licenseDayTo } from '@dbrain/app-core/license-days';

/**
 * Phase 16a (ADR-0023, B2cPlatformPaketler): Platform's plan cards as the operator types them, and the days of Pro
 * given by hand. The fields are text (prices in liras with the locale's marks, counts as digits); a card's body is
 * read from them and checked against the API's own schema, so the screen refuses what the server would.
 */
export type PlanField = keyof PlatformPlanBody;

export interface PlanDraft {
  /** "500,00" */
  monthlyPriceKurus: string;
  /** "5.000,00"; empty: no yearly price */
  yearlyPriceKurus: string;
  /** digits only */
  dailyTokens: string;
  maxDocuments: string;
  maxFileMb: string;
  /** 16c.1: digits; empty: no limit ("Sınırsız"), 0: none (B2cModelPaketler) */
  maxOwnSkills: string;
  maxOwnConnections: string;
  /** 16c.1: the package's chat models (Ücretsiz exactly one) and the one a new chat starts with */
  chatModels: string[];
  defaultChatModel: string;
}

export type PlanFieldError =
  | { kind: 'required' }
  | { kind: 'price' }
  | { kind: 'min'; value: number }
  | { kind: 'max'; value: number }
  | { kind: 'invalid' }
  // 16c.1: Ücretsiz has one chat model; Pro at least one, the new chat's among them
  | { kind: 'oneModel' }
  | { kind: 'someModel' };

export interface PlanCheck {
  /** null while a field is not valid */
  body: PlatformPlanBody | null;
  errors: Partial<Record<PlanField, PlanFieldError>>;
}

const tag = (locale: string) => (locale.startsWith('en') ? 'en-US' : 'tr-TR');
const escape = (mark: string) => (mark === '.' ? '\\.' : mark);

/** 50000 kuruş → "500,00" (Turkish), "500.00" (English): a price field's text, without the currency */
export function formatLira(kurus: number, locale = 'tr'): string {
  return (kurus / 100).toLocaleString(tag(locale), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * A price as typed → kuruş: "5.000,00", "5000", "500,5" in Turkish ("5,000.00" in English). The other mark alone with
 * one or two digits after it is a decimal mark too ("500.50" in Turkish). null: not a price.
 */
export function parseLira(text: string, locale = 'tr'): number | null {
  const en = locale.startsWith('en');
  const group = en ? ',' : '.';
  const decimal = en ? '.' : ',';
  // \s covers the no-break space of a pasted "5.000,00 TL"
  let value = text.replace(/\s/g, '').replace(/^₺/, '').replace(/(?:₺|tl)$/i, '');
  if (value === '') return null;
  if (!value.includes(decimal) && new RegExp(`^\\d+${escape(group)}\\d{1,2}$`).test(value)) value = value.replace(group, decimal);
  const fraction = `(?:${escape(decimal)}\\d{0,2})?`;
  const grouped = new RegExp(`^\\d{1,3}(?:${escape(group)}\\d{3})+${fraction}$`);
  const plain = new RegExp(`^\\d+${fraction}$`);
  if (!grouped.test(value) && !plain.test(value)) return null;
  const [whole = '', cents = ''] = value.split(decimal);
  const kurus = Number(whole.split(group).join('')) * 100 + Number(cents.padEnd(2, '0'));
  return Number.isSafeInteger(kurus) ? kurus : null;
}

/** "2000000" → "2.000.000", as a count field shows its digits while they are typed */
export function groupDigits(digits: string, locale = 'tr'): string {
  return digits ? Number(digits).toLocaleString(tag(locale)) : '';
}

/** The digits of what was typed, at most `max` of them */
export const digitsOf = (text: string, max = 12) => text.replace(/\D/g, '').slice(0, max);

/** A card's fields as the plan is saved now */
export function draftOf(plan: Pick<PlatformPlanDto, PlanField>, locale = 'tr'): PlanDraft {
  return {
    monthlyPriceKurus: formatLira(plan.monthlyPriceKurus, locale),
    yearlyPriceKurus: plan.yearlyPriceKurus === null ? '' : formatLira(plan.yearlyPriceKurus, locale),
    dailyTokens: String(plan.dailyTokens),
    maxDocuments: String(plan.maxDocuments),
    maxFileMb: String(plan.maxFileMb),
    maxOwnSkills: plan.maxOwnSkills === null ? '' : String(plan.maxOwnSkills),
    maxOwnConnections: plan.maxOwnConnections === null ? '' : String(plan.maxOwnConnections),
    chatModels: [...plan.chatModels],
    defaultChatModel: plan.defaultChatModel ?? plan.chatModels[0] ?? '',
  };
}

/** The values saved now, as the API takes them */
export function bodyOf(plan: Pick<PlatformPlanDto, PlanField>): PlatformPlanBody {
  return {
    monthlyPriceKurus: plan.monthlyPriceKurus,
    yearlyPriceKurus: plan.yearlyPriceKurus,
    dailyTokens: plan.dailyTokens,
    maxDocuments: plan.maxDocuments,
    maxFileMb: plan.maxFileMb,
    maxOwnSkills: plan.maxOwnSkills,
    maxOwnConnections: plan.maxOwnConnections,
    ...(plan.chatModels.length > 0 ? { chatModels: [...plan.chatModels], defaultChatModel: plan.defaultChatModel ?? plan.chatModels[0] ?? '' } : {}),
  };
}

export function sameBody(a: PlatformPlanBody, b: PlatformPlanBody): boolean {
  return (Object.keys(a) as PlanField[]).every((field) => {
    const x = a[field];
    const y = b[field];
    return Array.isArray(x) && Array.isArray(y) ? x.join('|') === y.join('|') : x === y;
  });
}

/**
 * A card's body from its fields. Ücretsiz has no price (the API refuses one); Pro's yearly price may be left empty.
 * The ranges are the API's schema's, so its limits are said once.
 */
export function checkDraft(id: PlanId, draft: PlanDraft, locale = 'tr', savedModels = 1): PlanCheck {
  const errors: PlanCheck['errors'] = {};
  const price = (field: 'monthlyPriceKurus' | 'yearlyPriceKurus', optional: boolean): number | null => {
    const text = draft[field].trim();
    if (text === '') {
      if (!optional) errors[field] = { kind: 'required' };
      return null;
    }
    const kurus = parseLira(text, locale);
    if (kurus === null) errors[field] = { kind: 'price' };
    return kurus;
  };
  const count = (field: 'dailyTokens' | 'maxDocuments' | 'maxFileMb'): number => {
    const digits = draft[field].replace(/\D/g, '');
    if (digits === '') errors[field] = { kind: 'required' };
    return digits === '' ? 0 : Number(digits);
  };
  // 16c.1: an own limit left empty has no limit
  const limit = (field: 'maxOwnSkills' | 'maxOwnConnections'): number | null => {
    const digits = draft[field].replace(/\D/g, '');
    return digits === '' ? null : Number(digits);
  };
  const free = id === 'free';
  // a package with no model yet (none was ever open in chat; the first one opened becomes its own) saves without one
  const noModels = draft.chatModels.length === 0 && savedModels === 0;
  if (!noModels && free && draft.chatModels.length !== 1) errors.chatModels = { kind: 'oneModel' };
  if (!noModels && !free && draft.chatModels.length === 0) errors.chatModels = { kind: 'someModel' };
  const models = noModels
    ? {}
    : {
        chatModels: draft.chatModels,
        // Ücretsiz's one model is its new chat's; Pro's falls back to its first while the chosen one is not on the list
        defaultChatModel: free || !draft.chatModels.includes(draft.defaultChatModel) ? (draft.chatModels[0] ?? '') : draft.defaultChatModel,
      };
  const candidate = {
    monthlyPriceKurus: free ? 0 : (price('monthlyPriceKurus', false) ?? 0),
    yearlyPriceKurus: free ? null : price('yearlyPriceKurus', true),
    dailyTokens: count('dailyTokens'),
    maxDocuments: count('maxDocuments'),
    maxFileMb: count('maxFileMb'),
    maxOwnSkills: limit('maxOwnSkills'),
    maxOwnConnections: limit('maxOwnConnections'),
    ...models,
  };
  const parsed = platformPlanBody.safeParse(candidate);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path[0] as PlanField;
      if (errors[field]) continue;
      errors[field] =
        issue.code === 'too_small' ? { kind: 'min', value: Number(issue.minimum) } : issue.code === 'too_big' ? { kind: 'max', value: Number(issue.maximum) } : { kind: 'invalid' };
    }
  }
  return parsed.success && Object.keys(errors).length === 0 ? { body: parsed.data, errors } : { body: null, errors };
}

export type LimitField = keyof PlanLimitsDto;
// 16c.1: connections are a count (their on/off follows it)
const LIMIT_FIELDS: readonly LimitField[] = ['dailyTokens', 'maxDocuments', 'maxFileMb', 'maxOwnSkills', 'maxOwnConnections'];

/** What an announced change of Ücretsiz will change (D-270): the limits whose coming value differs from today's */
export function pendingRows(plan: Pick<PlatformPlanDto, LimitField | 'pendingChange'>): { field: LimitField; from: number | boolean | null; to: number | boolean | null }[] {
  const next = plan.pendingChange?.next;
  if (!next) return [];
  return LIMIT_FIELDS.filter((field) => next[field] !== plan[field]).map((field) => ({ field, from: plan[field], to: next[field] }));
}

// Pro by hand (D-269) ------------------------------------------------------------------------------------------------

/**
 * The days the "Son gün" field offers: the API ends Pro at the next midnight after that day (Istanbul) and refuses an
 * end more than 366 days away, so the last day is at most 365 days after today.
 */
export const GRANT_DAYS = { default: 30, last: 365 } as const;
export const GRANT_ZONE = DEFAULT_COMPANY_TIME_ZONE;

/** The day `days` days after today in the zone, as a date field's value (YYYY-MM-DD) */
export function dayAfter(now: Date, days: number, timeZone: string = GRANT_ZONE): string {
  const today = zonedParts(now, timeZone);
  return new Date(Date.UTC(today.year, today.month - 1, today.day + days)).toISOString().slice(0, 10);
}

/** Pro "until 30 November" ends on 1 December at 00:00: its last day, YYYY-MM-DD */
export function lastDayOf(endsAt: string, timeZone: string = GRANT_ZONE): string {
  return zonedDateKey(new Date(Date.parse(endsAt) - 1), timeZone);
}

/** A grant's last day as typed: a day from today to the last one offered */
export function grantDayValid(day: string, now: Date, timeZone: string = GRANT_ZONE): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= dayAfter(now, 0, timeZone) && day <= dayAfter(now, GRANT_DAYS.last, timeZone);
}

const ONES_DATIVE = ['', 'e', 'ye', 'e', 'e', 'e', 'ya', 'ye', 'e', 'a'];
const TENS_DATIVE = ['', 'a', 'ye', 'a', 'a', 'ye', 'a', 'e', 'e', 'a'];

/** The Turkish dative ending of a year read aloud, by its last spoken word: 2026'ya, 2027'ye, 2030'a, 2000'e */
export function yearDative(year: number): string {
  const n = Math.abs(Math.round(year));
  if (n % 10 !== 0) return ONES_DATIVE[n % 10] ?? 'e';
  if (n % 100 !== 0) return TENS_DATIVE[(n % 100) / 10] ?? 'e';
  return 'e';
}

/**
 * Pro's last day in "… kadar": "30 Kasım'a" in this year, "2 Kasım 2027'ye" in another (English "30 November",
 * "2 November 2027"; the sentence says "until").
 */
export function untilDay(day: string, locale: string, thisYear: number): string {
  const year = Number(day.slice(0, 4));
  if (year === thisYear) return licenseDayTo(day, locale);
  const text = licenseDay(day, locale, true);
  return locale.startsWith('tr') ? `${text}'${yearDative(year)}` : text;
}
